/**
 * The edits the paragraph editor makes (R18; spec 2026-10-23 §5 Tab 2:
 * "a list of paragraphs (add, remove, reorder; 1–12 paragraphs)").
 *
 * One text box per paragraph, and a paragraph is one block of text: the server
 * refuses any control character, newline included. So:
 *   - Enter splits the paragraph at the caret (a selection is replaced);
 *   - a paste with line breaks becomes paragraphs, empty lines dropped;
 *   - a line break that gets in another way (a drop, autocorrect) is split the
 *     same way, so no line break ever reaches the server;
 *   - a tab becomes a space; any other control character is removed.
 * Past WELCOME_MAX_PARAGRAPHS every edit is refused with plain words.
 *
 * Every function returns a NEW array (never changes its input) and says where
 * focus goes next, so the component only applies the result.
 */
import { WELCOME_MAX_PARAGRAPHS, WELCOME_MIN_PARAGRAPHS } from './contract';
import { CONTROL_CHARS } from './welcome-text';

export interface FocusTarget {
  index: number;
  caret: number;
}

export type EditResult =
  | { ok: true; paragraphs: string[]; focus: FocusTarget | null }
  | { ok: false; error: string };

const TOO_MANY = `A welcome can have at most ${WELCOME_MAX_PARAGRAPHS} paragraphs.`;
const ADD_REFUSED = `${TOO_MANY} Remove a paragraph before you add a new one.`;

const LINE_BREAK = /\r\n|\r|\n|\u2028|\u2029/;
// eslint-disable-next-line no-control-regex
const OTHER_CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/g;

/** Tabs become spaces; control characters other than line breaks go. */
function clean(text: string): string {
  return text.replace(/\t/g, ' ').replace(OTHER_CONTROL, '');
}

function hasBreakOrControl(text: string): boolean {
  return CONTROL_CHARS.test(text) || /[\u2028\u2029]/.test(text);
}

/**
 * Replace paragraph `index` with the lines of `before + inserted + after`.
 * The caret lands just after the inserted text.
 */
function spliceLines(paragraphs: readonly string[], index: number, before: string, inserted: string, after: string, what: 'paste' | 'text'): EditResult {
  const lines = clean(`${before}${inserted}${after}`)
    .split(LINE_BREAK)
    .filter((line) => line.trim() !== '');
  const parts = lines.length > 0 ? lines : [''];
  const total = paragraphs.length - 1 + parts.length;
  if (total > WELCOME_MAX_PARAGRAPHS) {
    return what === 'paste'
      ? { ok: false, error: `This paste makes ${total} paragraphs. ${TOO_MANY} Paste less text, or remove paragraphs first.` }
      : { ok: false, error: `This text makes ${total} paragraphs. ${TOO_MANY} Remove the line breaks, or remove paragraphs first.` };
  }
  const next = [...paragraphs.slice(0, index), ...parts, ...paragraphs.slice(index + 1)];
  const last = parts[parts.length - 1];
  const tail = clean(after);
  const caret = Math.max(0, last.endsWith(tail) ? last.length - tail.length : last.length);
  return { ok: true, paragraphs: next, focus: { index: index + parts.length - 1, caret } };
}

/** Enter (without Shift): split paragraph `index` at the caret. A selection is removed. */
export function splitAt(paragraphs: readonly string[], index: number, selStart: number, selEnd: number): EditResult {
  if (paragraphs.length >= WELCOME_MAX_PARAGRAPHS) return { ok: false, error: ADD_REFUSED };
  const text = paragraphs[index] ?? '';
  const before = text.slice(0, Math.min(selStart, selEnd)).trimEnd();
  const after = text.slice(Math.max(selStart, selEnd)).trimStart();
  const next = [...paragraphs.slice(0, index), before, after, ...paragraphs.slice(index + 1)];
  return { ok: true, paragraphs: next, focus: { index: index + 1, caret: 0 } };
}

/**
 * A paste (or a drop) of `inserted` over the selection of paragraph `index`.
 * Null when the text has no line break and no control character: the browser
 * inserts it as usual and keeps its own undo history.
 */
export function insertText(
  paragraphs: readonly string[],
  index: number,
  selStart: number,
  selEnd: number,
  inserted: string,
): EditResult | null {
  if (!hasBreakOrControl(inserted)) return null;
  const text = paragraphs[index] ?? '';
  const before = text.slice(0, Math.min(selStart, selEnd));
  const after = text.slice(Math.max(selStart, selEnd));
  return spliceLines(paragraphs, index, before, inserted, after, 'paste');
}

/**
 * The text box's new value (typing). Plain text replaces the paragraph and
 * focus stays where it is. A line break or a control character that got in
 * anyway is handled like a paste.
 */
export function replaceText(paragraphs: readonly string[], index: number, value: string): EditResult {
  if (!hasBreakOrControl(value)) {
    const next = [...paragraphs];
    next[index] = value;
    return { ok: true, paragraphs: next, focus: null };
  }
  return spliceLines(paragraphs, index, '', value, '', 'text');
}

/** A new empty paragraph after `index`. */
export function addAfter(paragraphs: readonly string[], index: number): EditResult {
  if (paragraphs.length >= WELCOME_MAX_PARAGRAPHS) return { ok: false, error: ADD_REFUSED };
  const next = [...paragraphs.slice(0, index + 1), '', ...paragraphs.slice(index + 1)];
  return { ok: true, paragraphs: next, focus: { index: index + 1, caret: 0 } };
}

/** Remove paragraph `index`. Focus goes to the paragraph before it (or the new first one), at its end. */
export function removeAt(paragraphs: readonly string[], index: number): EditResult {
  if (paragraphs.length <= WELCOME_MIN_PARAGRAPHS) {
    return {
      ok: false,
      error: `A welcome needs at least ${WELCOME_MIN_PARAGRAPHS} paragraph. Change the text of this paragraph instead of removing it.`,
    };
  }
  const next = paragraphs.filter((_, i) => i !== index);
  const focus = Math.max(0, index - 1);
  return { ok: true, paragraphs: next, focus: { index: focus, caret: next[focus].length } };
}

/** Move paragraph `index` one place up (-1) or down (+1). Focus follows the paragraph. */
export function move(paragraphs: readonly string[], index: number, delta: -1 | 1): EditResult {
  const target = index + delta;
  if (target < 0) return { ok: false, error: `Paragraph ${index + 1} is already the first paragraph.` };
  if (target >= paragraphs.length) return { ok: false, error: `Paragraph ${index + 1} is already the last paragraph.` };
  const next = [...paragraphs];
  [next[index], next[target]] = [next[target], next[index]];
  return { ok: true, paragraphs: next, focus: { index: target, caret: next[target].length } };
}
